
import store from '@/store'
import enums, { BloodGroupWord, BloodRhWord, CrossMatchingResult, TestResult } from '@/lib/enums'
import _ from 'lodash'

export function getFieldValue ({ fieldType, enumType, word, dic, fieldText }) {
  if (fieldType === 'word') {
    return store?.getters['bis/word/getWordByText'](word, fieldText)?.id
  } else if (fieldType === 'enum') {
    return enums[enumType]?.getItemValue(fieldText)
  } else if (fieldType === 'dic') {
    console.error(`暂未实现字段类型dic的文本解析(字段值:${fieldText})`)
  } else {
    console.error(`无法识别的字段类型:${fieldType}(字段文本:${fieldText})`)
  }
}

export function getFieldText ({ fieldType, enumType, word, dic, fieldValue }) {
  if (fieldType === 'word') {
    return store?.getters['bis/word/getWordText'](fieldValue)
  } else if (fieldType === 'enum') {
    return enums[enumType]?.getItemText(fieldValue)
  } else if (fieldType === 'dic') {
    console.error(`暂未实现字段类型dic的值解析(字段值:${fieldValue})`)
  } else {
    console.error(`无法识别的字段类型:${fieldType}(字段值:${fieldValue})`)
  }
}

function resolveTubeResults (tubeResults, fields) {
  if (!tubeResults || !fields?.length) return null
  let getWord = null
  let getWordByText = null
  const result = { }
  for (const field of fields) {
    const value = tubeResults[field]
    let resolvedValue
    if (_.isNumber(value)) {
      if (!getWord) {
        getWord = store?.getters['bis/word/getWord']
        if (!getWord) return null
      }
      resolvedValue = getWord(value)
    } else if (_.isString(value)) {
      if (!getWordByText) {
        getWordByText = store?.getters['bis/word/getWordByText']
        if (!getWordByText) return null
      }
      resolvedValue = getWordByText('experiment_tube_result', value)
    } else {
      resolvedValue = value
    }
    result[field] = resolvedValue
  }
  return result
}

function forwardBloodGroup (tubeResults) {
  const resolvedResults = resolveTubeResults(tubeResults, ['A.GD', 'B.GD'])
  if (!resolvedResults) return null
  const { 'A.GD': agd, 'B.GD': bgd } = resolvedResults
  if (!agd?.data || !bgd?.data || agd.data.invalid || bgd.data.invalid) return null
  if (agd.data.positive) {
    return BloodGroupWord.getItem(bgd.data.positive ? BloodGroupWord.AB : BloodGroupWord.A)
  } else {
    return BloodGroupWord.getItem(bgd.data.positive ? BloodGroupWord.B : BloodGroupWord.O)
  }
}
function reverseBloodGroup (tubeResults) {
  const resolvedResults = resolveTubeResults(tubeResults, ['A1.GI', 'B.GI'])
  if (!resolvedResults) return null
  const { 'A1.GI': a1gi, 'B.GI': bgi } = resolvedResults
  if (!a1gi?.data || !bgi?.data || a1gi.data.invalid || bgi.data.invalid) return null
  if (a1gi.data.positive) {
    return BloodGroupWord.getItem(bgi.data.positive ? BloodGroupWord.O : BloodGroupWord.B)
  } else {
    return BloodGroupWord.getItem(bgi.data.positive ? BloodGroupWord.A : BloodGroupWord.AB)
  }
}
function resolveRh (tubeResults, rhTubeTitle) {
  const resolvedResults = resolveTubeResults(tubeResults, [rhTubeTitle])
  const rhTubeModel = resolvedResults && resolvedResults[rhTubeTitle]
  if (!rhTubeModel?.data || rhTubeModel.data.invalid) return null
  if (rhTubeModel.data.positive) {
    return BloodRhWord.getItem(BloodRhWord.Positive)
  } else if (rhTubeModel.data.negative) {
    return BloodRhWord.getItem(BloodRhWord.Negative)
  } else {
    return null
  }
}
function resolveCrossMatchingItem (tubeResults, crossMatchingItemTubeTitle) {
  const resolvedResults = resolveTubeResults(tubeResults, [crossMatchingItemTubeTitle])
  let crossMatchingItemTubeModel = resolvedResults && resolvedResults[crossMatchingItemTubeTitle]
  if (!crossMatchingItemTubeModel?.data || crossMatchingItemTubeModel.data.invalid) return null
  const getWordByText = store?.getters['bis/word/getWordByText']
  if (getWordByText) {
    crossMatchingItemTubeModel = getWordByText('cross_matching_item_result', crossMatchingItemTubeModel.text) ?? crossMatchingItemTubeModel
  }
  return crossMatchingItemTubeModel
}

export function computeBloodGroupIdByForwardReverse (forwardBloodGroupId, reverseBloodGroupId) {
  return forwardBloodGroupId === reverseBloodGroupId ? forwardBloodGroupId : BloodGroupWord.Unknown
}

export const experimentItemResultMethods = Object.freeze({
  forwardBloodGroup,
  reverseBloodGroup,
  bloodGroup (tubeResults) {
    const forward = forwardBloodGroup(tubeResults)
    if (!forward) return null
    const reverse = reverseBloodGroup(tubeResults)
    if (!reverse) return null
    return forward.value === reverse.value ? forward : BloodGroupWord.getItem(BloodGroupWord.Unknown)
  },
  ucRh (tubeResults) {
    return resolveRh(tubeResults, 'C.F')
  },
  udRh (tubeResults) {
    return resolveRh(tubeResults, 'D.RH')
  },
  ueRh (tubeResults) {
    return resolveRh(tubeResults, 'E.F')
  },
  crossMatchingMain (tubeResults) {
    return resolveCrossMatchingItem(tubeResults, 'X.X')
  },
  crossMatchingCross (tubeResults) {
    return resolveCrossMatchingItem(tubeResults, 'x.x')
  },
  crossMatching (tubeResults) {
    const resolvedResults = resolveTubeResults(tubeResults, ['X.X', 'x.x'])
    if (!resolvedResults) return null
    // const { 'X.X': { data: mainData }, 'x.x': { data: crossData } } = resolvedResults
    const { 'X.X': main, 'x.x': cross } = resolvedResults
    if (!main?.data || !cross?.data || main.data.invalid || cross.data.invalid) return CrossMatchingResult.getItem(CrossMatchingResult.Unknown)
    const mainData = main.data
    const crossData = cross.data
    if (mainData.negative && crossData.negative) {
      return CrossMatchingResult.getItem(CrossMatchingResult.Match)
    } else if (mainData.negative) {
      return CrossMatchingResult.getItem(CrossMatchingResult.UnmatchCross)
    } else if (crossData.negative) {
      return CrossMatchingResult.getItem(CrossMatchingResult.UnmatchMaster)
    } else {
      return CrossMatchingResult.getItem(CrossMatchingResult.Unmatch)
    }
  },
  antibodyScreening (tubeResults) {
    const resolvedResults = resolveTubeResults(tubeResults, ['I.IAC', 'II.IAC', 'III.IAC'])
    if (!resolvedResults) return null
    const results = [resolvedResults['I.IAC'], resolvedResults['II.IAC'], resolvedResults['III.IAC']]
    if (results.findIndex(result => !result?.data || result.data.invalid) >= 0) return null
    if (results.find(result => result.data.positive)) {
      return TestResult.getItem(TestResult.Positive)
    } else {
      return TestResult.getItem(TestResult.Negative)
    }
  }
})
